import type { MaybeRefOrGetter } from 'vue'
import { computed, ref, toValue, watch } from 'vue'
import type { AgentBranding, AgentPublic, AuthMethods } from 'models'
import { createChatbotApiClient } from 'composables'
import { resolveContrastColor } from 'components/src/chat/utils'
import { BACKEND_URL } from '~/utils'

export function useChatbotAgent(options: {
    baseUrl?: string
    basePath?: string
    organizationSlug?: MaybeRefOrGetter<string>
    agentId?: MaybeRefOrGetter<string>
    headers?: MaybeRefOrGetter<Record<string, string> | undefined>
    /**
     * Host-provided color scheme override, applied when the agent theme is
     * `auto`. Mirrors the precedence used by the chatbot store: agent theme,
     * then this override, then the OS `prefers-color-scheme`.
     */
    systemTheme?: MaybeRefOrGetter<'light' | 'dark' | undefined>
}) {
    const agent = ref<AgentPublic>()
    const branding = ref<AgentBranding>()
    const authMethods = ref<AuthMethods>()
    const requiresAuth = ref(false)
    const notFound = ref(false)
    const isLoading = ref(false)

    async function load() {
        const orgSlug = toValue(options.organizationSlug)
        const agentId = toValue(options.agentId)
        if (!orgSlug || !agentId) {
            return
        }
        const apiClient = createChatbotApiClient({
            baseUrl: options.baseUrl ?? BACKEND_URL,
            basePath: options.basePath,
            headers: () => toValue(options.headers),
        })
        isLoading.value = true
        try {
            const result = await apiClient.getAgentAccess(orgSlug, agentId)
            authMethods.value = result.authMethods
            if (result.requiresAuth) {
                requiresAuth.value = true
                branding.value = result.branding ?? undefined
                agent.value = undefined
            } else {
                requiresAuth.value = false
                branding.value = undefined
                agent.value = result.agent ?? undefined
            }
            notFound.value = false
        } catch {
            notFound.value = true
            agent.value = undefined
            branding.value = undefined
            requiresAuth.value = false
        } finally {
            isLoading.value = false
        }
    }

    // Reload when org/agent changes
    watch(
        [
            () => toValue(options.organizationSlug),
            () => toValue(options.agentId),
        ],
        () => {
            load()
        },
        { immediate: true },
    )

    // Reload when headers change meaningfully (e.g. after login) and auth is required
    watch(
        () => toValue(options.headers),
        () => {
            if (requiresAuth.value) {
                load()
            }
        },
    )

    // Branding-aware theming: fall back to branding when agent is not available
    const source = computed(
        () => agent.value?.interface ?? branding.value?.interface,
    )

    const contrastColor = computed(() =>
        resolveContrastColor(source.value?.textColor, source.value?.mainColor),
    )
    const mainColor = computed(() => source.value?.mainColor)

    const themeClass = computed(() => {
        const theme = source.value?.theme
        if (theme && theme !== 'auto') {
            return ['theme', `theme--${theme}`]
        }
        const systemTheme = toValue(options.systemTheme)
        if (systemTheme) {
            return ['theme', `theme--${systemTheme}`]
        }
        return ['theme']
    })

    const themeStyle = computed(() =>
        source.value
            ? {
                  '--chatbot-main-color': mainColor.value,
                  '--chatbot-contrast-color': contrastColor.value,
              }
            : undefined,
    )

    return {
        agent,
        branding,
        authMethods,
        requiresAuth,
        notFound,
        isLoading,
        reload: load,
        contrastColor,
        mainColor,
        themeClass,
        themeStyle,
    }
}
